import { formatRepoFiltersToApiFilters } from '@webapp/core/utils/repositories/formatRepoFiltersToApiFilters'
import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoFilterParams } from '@webapp/infrastructure/interfaces/repositories/RepoFilterParams'
import { type RepoPaginatedResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginatedResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IApproverRepository } from '@webapp/platform/approval-routing/business-logic/interfaces/IApproverRepository'
import { mapRequestableApproverApiModel } from '@webapp/platform/approval-routing/data-access/mapper/mapRequestableApproverApiModel'
import { type GetRequestableApproversInputData } from '@webapp/platform/approval-routing/data-access/types/GetRequestableApproversInputData'
import { type GetRequestableApproversPayload } from '@webapp/platform/approval-routing/data-access/types/GetRequestableApproversPayload'
import { type GetRequestableApproversResponse } from '@webapp/platform/approval-routing/data-access/types/GetRequestableApproversResponse'
import { type RequestableApproverConstructor } from '@webapp/platform/approval-routing/entities/RequestableApprover'

export const APPROVERS_URL = '/api/v3/approvers/'

interface Dependency {
  httpClient: IHttpClient
}

export class ApproverRepository implements IApproverRepository {
  constructor(private readonly deps: Dependency) {}

  async getRequestableApprovers(
    data: GetRequestableApproversInputData,
    params?: RepoFilterParams
  ): Promise<RepoPaginatedResponse<RequestableApproverConstructor>> {
    try {
      const res = await this.deps.httpClient.post<
        GetRequestableApproversResponse,
        GetRequestableApproversPayload & RepoFilterParams
      >(`${APPROVERS_URL}requestable/`, {
        account_code_ids: data.accountCodeIds,
        amount: data.amount,
        base_content_type_id: data.baseContentTypeId,
        department: data.secondaryCategoryId,
        ...formatRepoFiltersToApiFilters(params),
      })

      const { data: requestableApprovers, metadata } = res.data

      return {
        data:
          requestableApprovers.map((a) => mapRequestableApproverApiModel(a)) ??
          [],
        pagination: {
          count: metadata?.pagination?.count || 0,
          page: metadata?.pagination?.current_page || 0,
          pageSize: metadata?.pagination?.page_size || 0,
          numPages: metadata?.pagination?.num_pages || 0,
        },
      }
    } catch (err) {
      throwRepoErrorFromApiV3Error(err)
    }
  }
}
